/**
 * Heads-up display.
 *
 * After the latest mobile-cleanup pass this is essentially the
 * application brand strip (top-left) plus the interaction-hint chip
 * (bottom-left, viewport-adaptive). Everything else — mode toggle,
 * filters, edge controls, cluster strength, difficulty — lives in
 * SettingsMenu (top-right), and search lives in NavOverlay (also
 * top-right).
 *
 * Splitting the controls this way keeps the canvas top-left corner
 * very quiet: the user only sees the app name and a short hint until
 * they actively open a panel.
 */
import { BMAC_URL, donateConfigured } from './feedback-config';
import { FONT, INK, INK3, RADIUS, hexA, FLUX } from './tokens';
import { useViewport } from './useViewport';

export function Hud() {
  const { isMobile, isPortrait } = useViewport();
  const bottomSheet = isMobile && isPortrait;

  return (
    <>
      {/* Brand strip — top-left. Just the app name + build stamp; the
          "chart the grappling universe" line lives in the share-card
          / meta copy, not in the app chrome (it read cheesy under the
          brand). */}
      <div
        style={{
          position: 'fixed',
          top: 14,
          left: 14,
          zIndex: 30,
          color: INK,
          fontSize: 12,
          userSelect: 'none',
          display: 'flex',
          alignItems: 'center',
          gap: 10,
        }}
      >
        {/* Brand lockup: the orbit icon (top/bottom bodies) + wordmark.
            Icon inlined so it paints crisply and the "bjjmap" text keeps
            using the loaded Space Grotesk face. */}
        <span
          style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}
        >
          <svg
            width="24"
            height="24"
            viewBox="0 0 64 64"
            fill="none"
            role="img"
            aria-label="bjjmap"
          >
            <ellipse
              cx="32"
              cy="32"
              rx="28"
              ry="14"
              transform="rotate(-32 32 32)"
              stroke="#7aaec0"
              strokeWidth="2"
              opacity="0.55"
            />
            <circle cx="26" cy="27" r="12" fill="#d4a070" />
            <circle cx="39" cy="39" r="12" fill="#7aaec0" />
          </svg>
          <strong
            style={{
              fontFamily: FONT.display,
              fontWeight: 700,
              fontSize: 17,
              letterSpacing: '-0.02em',
            }}
          >
            bjjmap
          </strong>
        </span>
        {/*
          Environment badge. Empty label = production, and production
          shows nothing: a visitor should never have to think about which
          environment they are on, and an "everything is normal" badge is
          chrome that stops being read after the first look.

          Sits BEFORE the build stamp because it answers the coarser
          question. With three tabs open, "which environment" is what you
          scan for; the revision is what you check second.
        */}
        {__BJJMAP_ENV_LABEL__ !== '' && (
          <span
            title={`This is the ${__BJJMAP_ENV__} environment — not the live site`}
            style={{
              fontFamily: FONT.mono,
              fontSize: 10,
              letterSpacing: '0.08em',
              textTransform: 'uppercase',
              padding: '1px 6px',
              borderRadius: 999,
              // Warm and low-saturation: visible enough that you cannot
              // mistake staging for production, quiet enough that it does
              // not compete with the map for attention all day.
              color: 'rgba(255,196,140,0.95)',
              background: 'rgba(255,150,70,0.12)',
              border: '1px solid rgba(255,150,70,0.38)',
            }}
          >
            {__BJJMAP_ENV_LABEL__}
          </span>
        )}
        {/* Build stamp — only rendered when it's a REAL stamp
            (git / CI resolved). On the Docker production build git
            isn't reachable so the stamp falls through to "dev / local"
            (noise for a public visitor). Locally (`pnpm dev`) it
            resolves so devs still see `#172 · abc1234`. */}
        {__BJJMAP_BUILD_NUMBER__ !== 'dev' &&
          __BJJMAP_BUILD_SHA__ !== 'local' && (
            <span
              style={{
                fontFamily: FONT.mono,
                color: INK3,
                fontSize: 11,
                fontVariantNumeric: 'tabular-nums',
                cursor: 'pointer',
              }}
              title={`build ${__BJJMAP_BUILD_NUMBER__} (${__BJJMAP_BUILD_SHA__}) — tap to copy`}
              onClick={() => {
                const stamp = `#${__BJJMAP_BUILD_NUMBER__} · ${__BJJMAP_BUILD_SHA__}`;
                navigator.clipboard?.writeText(stamp).catch(() => {});
              }}
            >
              #{__BJJMAP_BUILD_NUMBER__} · {__BJJMAP_BUILD_SHA__}
            </span>
          )}
      </div>

      {/* Interaction hint — anchored to the canvas's bottom-left
          corner. In side-drawer mode (desktop / landscape mobile)
          the canvas occupies the left portion of the viewport, so
          the hint clings to viewport-bottom-left at all times. In
          bottom-sheet mode (mobile portrait) the canvas SHRINKS
          upward when the sheet opens; we add the sheet's height to
          the bottom offset so the hint rides the sheet boundary up
          instead of getting covered. Same CSS variable
          (--bjjmap-sheet-height) that drives EdgeActions and the
          PanelHandle in bottom-sheet mode. */}
      <div
        style={{
          position: 'fixed',
          // Portrait: bottom-align with the EdgeActions control buttons
          // on the right (same `sheet-height + 6px` offset). The hint text
          // is kept short (see below) so it stays left of the centred
          // grab-handle pill without needing extra vertical clearance.
          bottom: bottomSheet
            ? 'calc(var(--bjjmap-sheet-height, 0px) + 6px)'
            : 14,
          left: 14,
          zIndex: 30,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'flex-start',
          gap: 8,
          // Container passes pointer events through; children that
          // need clicks (the donate button) re-enable them.
          pointerEvents: 'none',
          // Match the sheet-open animation so this stack slides up
          // smoothly with the panel boundary rather than jumping.
          transition: 'bottom 220ms cubic-bezier(0.2, 0.8, 0.2, 1)',
        }}
      >
        {/* Buy Me a Coffee — icon-only support affordance, bottom-left.
            Only when a real BMAC URL is set. */}
        {donateConfigured && (
          <a
            href={BMAC_URL}
            target="_blank"
            rel="noopener noreferrer"
            aria-label="Buy me a coffee — support bjjmap"
            title="Buy me a coffee — support bjjmap"
            style={{
              pointerEvents: 'auto',
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: 32,
              height: 32,
              borderRadius: RADIUS.sm,
              background: hexA(FLUX, 0.1),
              border: `1px solid ${hexA(FLUX, 0.4)}`,
              color: '#f0d79a',
              textDecoration: 'none',
              cursor: 'pointer',
              backdropFilter: 'blur(6px)',
            }}
          >
            {/* Coffee cup glyph. */}
            <svg
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.7"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden="true"
            >
              <path d="M4 8h13v5a5 5 0 0 1-5 5H9a5 5 0 0 1-5-5V8z" />
              <path d="M17 9h2.5a2.5 2.5 0 0 1 0 5H17" />
              <line x1="7" y1="2.5" x2="7" y2="4.5" />
              <line x1="11" y1="2.5" x2="11" y2="4.5" />
            </svg>
          </a>
        )}

        {/* Interaction hint — bottom-most in the stack. On mobile it's
            kept SHORT so its bottom-left footprint can't reach the
            bottom-centre panel handle pill (the collision the long
            string caused on narrow portrait screens). Desktop keeps the
            fuller phrasing (side panel handle, no centre pill). */}
        <div
          style={{
            fontFamily: FONT.mono,
            color: INK3,
            fontSize: 10,
            letterSpacing: '0.06em',
            userSelect: 'none',
            // Hard ceiling on width in portrait so it stays left of the
            // centred panel-handle pill even if the text ever grows.
            maxWidth: bottomSheet ? 'calc(50vw - 52px)' : undefined,
            lineHeight: 1.4,
          }}
        >
          {isMobile
            ? 'drag · pinch · tap'
            : 'drag to orbit · scroll to zoom · click a node'}
        </div>
      </div>
    </>
  );
}
